import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CreditCard, Sparkle, CheckCircle, ArrowRight, Warning, Gear, Gift, XCircle } from "@phosphor-icons/react";
import client from "../lib/api";
import { startCheckout, openBillingPortal } from "../lib/billing";
import { trackStep } from "../lib/analytics";
import { useAuth } from "../context/AuthContext";
import { getStoredPromo } from "../lib/analytics";
import CancelDialog from "../components/billing/CancelDialog";

const TIER_META = {
  solo: { name: "Solo", price: 79, for: "1 truck" },
  duo: { name: "Duo", price: 129, for: "2 trucks" },
  trio: { name: "Trio", price: 179, for: "3 trucks" },
  small_fleet: { name: "Small Fleet", price: 299, for: "4-10 trucks" },
  growth: { name: "Growth", price: 499, for: "11-25 trucks" },
};

function daysUntil(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  return Math.ceil((d - new Date()) / (1000 * 60 * 60 * 24));
}

export default function BillingPage() {
  const { user, refreshUser } = useAuth();
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(null); // tier being processed
  const [portalBusy, setPortalBusy] = useState(false);
  const [winBack, setWinBack] = useState(false);
  const [interval, setInterval] = useState("monthly");
  const [annualBusy, setAnnualBusy] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [resumeBusy, setResumeBusy] = useState(false);

  async function handleResume() {
    setResumeBusy(true);
    try {
      await client.post("/billing/resume");
      toast.success("Welcome back — your plan continues as before.");
      await refreshUser?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't resume the plan.");
    } finally {
      setResumeBusy(false);
    }
  }

  // Direct ad landing (/start): ?auto=1&plan=solo → go straight to Stripe, no plan click.
  const [autoPlan] = useState(() => {
    const q = new URLSearchParams(window.location.search);
    let p = q.get("auto") === "1" ? q.get("plan") : null;
    if (!p) {
      try { if (sessionStorage.getItem("fp_auto_checkout") === "1") p = q.get("plan") || sessionStorage.getItem("chosen_plan"); } catch {}
    }
    return TIER_META[p] ? p : null;
  });
  const [autoState, setAutoState] = useState(autoPlan ? "waiting" : null); // waiting | launching | slow | failed | null

  useEffect(() => {
    if (!autoPlan || !user || autoState !== "waiting") return;
    const needsCard = !!user.needs_checkout || !user.subscription_status || user.subscription_status === "pending";
    try { sessionStorage.removeItem("fp_auto_checkout"); sessionStorage.removeItem("chosen_plan"); } catch {}
    if (!needsCard) { setAutoState(null); return; }
    setAutoState("launching");
    trackStep("checkout_start");
    const slow = setTimeout(() => setAutoState((s) => (s === "launching" ? "slow" : s)), 5000);
    startCheckout(autoPlan, "monthly").catch((e) => {
      clearTimeout(slow);
      setAutoState("failed");
      toast.error(e?.response?.data?.detail || "Couldn't open the card page automatically — tap the button to continue.");
    });
    return () => clearTimeout(slow);
  }, [autoPlan, user?.id, user?.needs_checkout, autoState]); // eslint-disable-line

  useEffect(() => {
    client.get("/billing/plans")
      .then((r) => setPlans(r.data.plans || []))
      .catch(() => setPlans([]));
    client.get("/billing/win-back-offer")
      .then((r) => setWinBack(!!r.data?.eligible))
      .catch(() => {});
    refreshUser?.().catch(() => {});
    trackStep("billing_view");
  }, []); // eslint-disable-line

  async function handleSubscribe(tier) {
    setBusy(tier);
    trackStep("checkout_start");
    try {
      await startCheckout(tier, interval);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start checkout. Try again.");
      setBusy(null);
    }
  }

  async function handlePortal() {
    setPortalBusy(true);
    try {
      await openBillingPortal();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't open billing portal.");
      setPortalBusy(false);
    }
  }

  async function handleSwitchAnnual() {
    if (!window.confirm("Switch to annual billing now? You'll be invoiced for a year (2 months free) with credit applied for unused time this month.")) return;
    setAnnualBusy(true);
    try {
      const r = await client.post("/billing/switch-annual");
      toast.success(`Switched to annual — $${Number(r.data?.annual_amount || 0).toLocaleString()}/yr, 2 months free 🎉`);
      await refreshUser?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't switch to annual.");
    } finally {
      setAnnualBusy(false);
    }
  }

  const status = user?.subscription_status || "pending";
  const startMode = !!user?.needs_checkout || new URLSearchParams(window.location.search).get("start") === "1";
  const promo = getStoredPromo();
  const isPending = startMode || status === "pending";
  const isActive = status === "active";
  const isPastDue = status === "past_due";
  const isTrial = status === "trialing" && !isPending;
  const isCanceled = status === "canceled" || status === "expired";
  const trialDays = daysUntil(user?.trial_ends_at);
  const currentTier = user?.plan;
  const pendingCancel = !!user?.cancel_at_period_end && (isTrial || isActive || isPastDue);
  const cancelEnds = user?.cancel_ends_at ? new Date(user.cancel_ends_at).toLocaleDateString() : null;

  return (
    <div className="space-y-6" data-testid="billing-page">
      <CancelDialog open={cancelOpen} onOpenChange={setCancelOpen} user={user} onDone={() => refreshUser?.().catch(() => {})} />
      {autoPlan && autoState && autoState !== "waiting" && (
        <div className="card-surface p-5 sm:p-6 border border-[#FF5A1F]/50 bg-[#FF5A1F]/[0.08]" data-testid="billing-auto-checkout" data-state={autoState}>
          <div className="flex flex-wrap items-center gap-4">
            {autoState === "launching" || autoState === "slow"
              ? <span className="h-9 w-9 rounded-full border-2 border-[#FF5A1F] border-t-transparent animate-spin shrink-0" aria-hidden />
              : <Warning size={32} weight="fill" className="text-[#FF9764] shrink-0" />}
            <div className="flex-1 min-w-[220px]">
              <div className="text-white font-semibold text-lg leading-tight">
                {autoState === "failed" ? "One more tap to add your card" : "Taking you to Stripe's secure card page…"}
              </div>
              <div className="text-neutral-300 text-sm mt-1">
                {TIER_META[autoPlan].name} plan · $0 today · ${TIER_META[autoPlan].price}/mo from day 8 · cancel anytime before then.
              </div>
            </div>
            {(autoState === "slow" || autoState === "failed") && (
              <button type="button" onClick={() => handleSubscribe(autoPlan)} disabled={busy === autoPlan}
                className="btn-primary rounded-full px-5 py-2.5 text-sm inline-flex items-center gap-2 disabled:opacity-60" data-testid="billing-auto-checkout-continue">
                <CreditCard size={16} /> Continue to card page <ArrowRight size={14} weight="bold" />
              </button>
            )}
          </div>
        </div>
      )}
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold text-white tracking-tight">{isPending ? "Start your 7-day free trial" : "Billing & subscription"}</h1>
        <p className="text-neutral-400 mt-2 text-sm sm:text-base">{isPending ? "Pick a plan. Add a card. $0 today — nothing is charged until day 8, and you can cancel in one click before then." : "Manage your FreightPilot AI plan, upgrade, or cancel anytime."}</p>
      </div>

      {isPending && (
        <div className="card-surface p-4 sm:p-5 border border-[#FF5A1F]/40 bg-[#FF5A1F]/[0.06]" data-testid="billing-start-trial-banner">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
            <span className="inline-flex items-center gap-1.5 text-white font-semibold"><CreditCard size={16} /> Card required · 7 days free · cancel anytime</span>
            <span className="text-neutral-400">Full access to every feature during the trial.</span>
            {promo && <span className="inline-flex items-center gap-1.5 text-emerald-300 font-semibold" data-testid="billing-promo-applied"><Gift size={14} weight="fill" /> Code {promo} applied — 50% off your first month after the trial</span>}
          </div>
        </div>
      )}

      {winBack && !isActive && (
        <div className="card-surface p-4 sm:p-5 border border-[#FF5A1F]/40 bg-[#FF5A1F]/[0.08] flex items-center gap-3"
             data-testid="winback-offer-banner">
          <span className="shrink-0 w-10 h-10 rounded-full bg-[#FF5A1F]/15 flex items-center justify-center">
            <Gift size={20} weight="fill" color="#FF9764" />
          </span>
          <div className="text-sm text-white">
            <b className="text-[#FF9764]">Welcome-back offer:</b> 20% off your first month —
            applied automatically at checkout, no code needed.
          </div>
        </div>
      )}

      {/* Status banner */}
      <div className={`card-surface p-6 sm:p-8 ${isPastDue ? "border-red-500/40" : ""}`} data-testid="billing-status-card">
        <div className="flex items-start gap-4">
          <div className={`shrink-0 w-11 h-11 rounded-full flex items-center justify-center ${
            isActive ? "bg-emerald-500/15 text-emerald-400" :
            isPastDue ? "bg-red-500/15 text-red-400" :
            isTrial ? "bg-[#FF5A1F]/15 text-[#FF5A1F]" :
            "bg-white/5 text-neutral-400"
          }`}>
            {isActive && <CheckCircle size={22} weight="fill" />}
            {isPastDue && <Warning size={22} weight="fill" />}
            {isTrial && <Sparkle size={22} weight="fill" />}
            {isCanceled && <CreditCard size={22} />}
          </div>
          <div className="flex-1">
            {isActive && (
              <>
                <div className="text-white font-semibold text-lg">Active — {TIER_META[currentTier]?.name || "Subscription"}</div>
                <p className="text-neutral-400 text-sm mt-1">
                  ${TIER_META[currentTier]?.price}/month · Next billing: {user?.current_period_end ? new Date(user.current_period_end).toLocaleDateString() : "—"}
                </p>
                {user?.billing_interval !== "year" && (
                  <button
                    onClick={handleSwitchAnnual}
                    disabled={annualBusy}
                    data-testid="billing-switch-annual-btn"
                    className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 border border-emerald-400/40 px-4 py-2 text-xs font-bold text-emerald-300 hover:bg-emerald-500/25 transition-colors disabled:opacity-50"
                  >
                    <Sparkle size={13} weight="fill" />
                    {annualBusy ? "Switching…" : "Switch to annual — 2 months free"}
                  </button>
                )}
              </>
            )}
            {isTrial && (
              <>
                <div className="text-white font-semibold text-lg">Free trial · {trialDays != null ? `${trialDays} day${trialDays === 1 ? "" : "s"} left` : "Active"}</div>
                <p className="text-neutral-400 text-sm mt-1">
                  {trialDays > 0
                    ? `Your card is on file. ${TIER_META[currentTier]?.name ? `${TIER_META[currentTier].name} starts billing` : "Billing starts"} when the trial ends — cancel anytime before then and you pay nothing.`
                    : "Your trial has ended. Pick a plan below to keep dispatching."}
                </p>
              </>
            )}
            {isPending && (
              <>
                <div className="text-white font-semibold text-lg" data-testid="billing-status-pending">No plan yet</div>
                <p className="text-neutral-400 text-sm mt-1">Choose a plan below to start your 7-day free trial. You'll enter a card on Stripe's secure page; $0 is charged today.</p>
              </>
            )}
            {isPastDue && (
              <>
                <div className="text-white font-semibold text-lg">Payment past due</div>
                <p className="text-neutral-400 text-sm mt-1">Your last payment failed. Update your card in the billing portal to keep your account active.</p>
              </>
            )}
            {isCanceled && (
              <>
                <div className="text-white font-semibold text-lg">Subscription canceled</div>
                <p className="text-neutral-400 text-sm mt-1">Your subscription is no longer active. Pick a plan below to reactivate.</p>
              </>
            )}
            {pendingCancel && (
              <div className="mt-3 rounded-xl border border-white/10 bg-white/[0.03] p-3 flex flex-wrap items-center gap-3" data-testid="billing-pending-cancel">
                <XCircle size={18} className="text-neutral-400 shrink-0" />
                <div className="text-sm text-neutral-300 flex-1 min-w-[200px]">
                  {isTrial ? "Trial canceled — your card won't be charged." : "Plan canceled — no further charges."} Access runs until <b className="text-white">{cancelEnds || "the end of your period"}</b>.
                </div>
                <button type="button" onClick={handleResume} disabled={resumeBusy} data-testid="billing-resume-btn"
                  className="rounded-full px-4 py-2 text-xs font-semibold bg-[#FF5A1F] text-black hover:bg-[#FF7038] disabled:opacity-50">
                  {resumeBusy ? "Resuming…" : "Keep my plan"}
                </button>
              </div>
            )}
          </div>
          {(isActive || isPastDue || isTrial) && (
            <div className="flex flex-col items-end gap-2">
              <button
                onClick={handlePortal}
                disabled={portalBusy}
                data-testid="billing-portal-btn"
                className="rounded-full px-4 py-2 text-xs sm:text-sm border border-white/15 text-neutral-200 hover:text-white hover:border-white/30 transition-colors inline-flex items-center gap-1.5 disabled:opacity-50"
              >
                <Gear size={13} /> {portalBusy ? "Opening…" : "Manage billing"}
              </button>
              {!pendingCancel && user?.is_owner !== false && (
                <button type="button" onClick={() => setCancelOpen(true)} data-testid="billing-cancel-btn"
                  className="text-xs text-neutral-500 hover:text-neutral-300 transition-colors inline-flex items-center gap-1">
                  <XCircle size={12} /> {isTrial ? "Cancel trial" : "Cancel plan"}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Plans grid */}
      <div>
        <div className="flex items-center gap-4 mb-3">
          <div className="text-xs uppercase tracking-widest text-neutral-500">CHOOSE A PLAN</div>
          <div className="ml-auto inline-flex rounded-full border border-white/10 bg-white/[0.03] p-1" data-testid="billing-interval-toggle">
            <button
              type="button"
              onClick={() => setInterval("monthly")}
              data-testid="billing-interval-monthly"
              className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${
                interval === "monthly" ? "bg-[#FF5A1F] text-black" : "text-neutral-400 hover:text-white"
              }`}
            >
              Monthly
            </button>
            <button
              type="button"
              onClick={() => setInterval("annual")}
              data-testid="billing-interval-annual"
              className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${
                interval === "annual" ? "bg-[#FF5A1F] text-black" : "text-neutral-400 hover:text-white"
              }`}
            >
              Annual <span className={interval === "annual" ? "opacity-80" : "text-emerald-400"}>· 2 months free</span>
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="billing-plans-grid">
          {plans.map((p) => {
            const isCurrent = isActive && currentTier === p.tier;
            const canSubscribe = !isCurrent;
            const annual = interval === "annual" && p.annual_amount != null;
            return (
              <div
                key={p.tier}
                className={`card-surface p-6 flex flex-col ${isCurrent ? "border-emerald-400/40 ring-1 ring-emerald-400/20" : ""}`}
                data-testid={`billing-tier-${p.tier}`}
              >
                {isCurrent && (
                  <div className="text-emerald-400 text-[10px] font-bold uppercase tracking-widest mb-3 inline-flex items-center gap-1">
                    <CheckCircle size={12} weight="fill" /> Current plan
                  </div>
                )}
                <div className="font-display text-xl font-semibold text-white">{p.name}</div>
                <div className="text-xs text-neutral-500 mt-1">{p.for}</div>
                <div className="mt-4 mb-1 flex items-baseline gap-1">
                  <span className="text-neutral-500">$</span>
                  <span className="font-display text-3xl font-semibold text-white" data-testid={`billing-price-${p.tier}`}>
                    {annual ? Math.round(p.annual_amount / 12) : p.amount}
                  </span>
                  <span className="text-neutral-500 text-sm">/mo</span>
                </div>
                <div className="mb-5 h-4 text-[11px]">
                  {annual ? (
                    <span className="text-emerald-400 font-semibold" data-testid={`billing-annual-note-${p.tier}`}>
                      ${p.annual_amount.toLocaleString()}/yr billed once · 2 months free
                    </span>
                  ) : (
                    <span className="text-neutral-600">billed monthly</span>
                  )}
                </div>
                <button
                  onClick={() => canSubscribe && handleSubscribe(p.tier)}
                  disabled={!canSubscribe || busy === p.tier}
                  data-testid={`billing-subscribe-${p.tier}`}
                  className={`w-full rounded-full px-5 py-2.5 text-sm font-semibold inline-flex items-center justify-center gap-2 transition-colors ${
                    isCurrent
                      ? "bg-white/5 text-neutral-500 cursor-default"
                      : "bg-[#FF5A1F] text-black hover:bg-[#FF7038] disabled:opacity-60"
                  }`}
                >
                  {isCurrent ? "Current plan" : busy === p.tier ? "Redirecting…" : (isActive ? "Switch" : isPending ? "Start 7-day free trial" : "Subscribe")}
                  {!isCurrent && <ArrowRight size={13} weight="bold" />}
                </button>
              </div>
            );
          })}
        </div>
        <p className="text-xs text-neutral-500 mt-4">
          All plans include unlimited AI-drafted messages, PDF invoicing, driver portal, and public tracking. Cancel anytime.
        </p>
      </div>
    </div>
  );
}
